import React from 'react';
import { Alert, Button, Divider, message, Space, Typography } from 'antd';
import { useQueryClient } from 'react-query';

import { CopyOutlined, SendOutlined } from '@ant-design/icons';

import { usePost } from '../../utils/reactQuery';
import { pathToUrl } from '../../utils/router';
import { ApiRoutesEnum } from '../../utils/routes';
import { CandidateType } from '../candidates/Candidates.types';

export const CandidateConsentsLink: React.FC<{
  candidateId: string;
  link: CandidateType['activeCandidateTokenLink'];
  email: CandidateType['email'];
}> = ({ candidateId, link, email }) => {
  const queryClient = useQueryClient();
  const invalidateCandidateQuery = () =>
    queryClient.invalidateQueries(pathToUrl(ApiRoutesEnum.getCandidate, { id: candidateId }));

  const { mutate: generateLink, isLoading: isGenerating } = usePost({ url: `/candidate/token/${candidateId}` });
  const {
    mutate: sendEmail,
    isLoading: isSending,
    isSuccess: isSent,
  } = usePost({ url: `/candidate/token/email/${candidateId}` });

  if (!link) {
    return (
      <>
        <Divider />
        <Space direction="vertical" align="center" style={{ width: '100%' }}>
          <Typography.Title level={5}>No generated link</Typography.Title>
          <Button
            type="primary"
            disabled={isGenerating}
            onClick={() => {
              generateLink(
                {},
                {
                  onSuccess: () => {
                    invalidateCandidateQuery();
                  },
                }
              );
            }}
          >
            Generate link to consents
          </Button>
        </Space>
        <Divider />
      </>
    );
  }

  return (
    <>
      <Divider />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">Link to consents</Typography.Text>

        <Button
          icon={<CopyOutlined />}
          onClick={async () => {
            await navigator.clipboard.writeText(link.generatedLink);
            message.success('Copied!');
          }}
        >
          Copy link
        </Button>
      </div>
      <Typography.Text strong>{link.generatedLink}</Typography.Text>

      <Divider />

      {email ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography.Text type="secondary">Send link via e-mail</Typography.Text>
            <Button
              icon={<SendOutlined />}
              disabled={isSending || isSent}
              onClick={() => {
                sendEmail(
                  {},
                  {
                    onError: (error) => {
                      message.error(`${error}`);
                    },
                  }
                );
              }}
            >
              {getSendLabel({ isSending, isSent })}
            </Button>
          </div>
          <Typography.Text strong>{email}</Typography.Text>
        </>
      ) : (
        <Alert
          showIcon
          type="warning"
          message="No email address"
          description="Add the candidate's email to send this link directly."
        />
      )}

      <Divider />
    </>
  );
};

const getSendLabel = ({ isSending, isSent }: { isSending: boolean; isSent: boolean }) => {
  if (isSending) return 'Sending...';
  if (isSent) return 'Sent!';
  return 'Send email';
};
